(() => {
  // src/lib/selling-plan-utils.ts
  var parsePlanMap = (jsonText) => {
    if (!jsonText) return {};
    try {
      const parsed = JSON.parse(jsonText);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch (e) {
      return {};
    }
  };
  var getVariantPlans = (map, variantId) => map[String(variantId)];
  var hasPlans = (variantPlans) => !!variantPlans && variantPlans.plans.length > 0;
  var UNIT_RANK = { day: 0, week: 1, month: 2, year: 3 };
  var planSortKey = (label) => {
    var _a;
    const lower = label.toLowerCase();
    const match = lower.match(/(\d+)?\s*(day|week|month|year)/);
    if (!match) return Number.MAX_SAFE_INTEGER;
    const count = match[1] ? parseInt(match[1], 10) : 1;
    const unit = (_a = UNIT_RANK[match[2]]) != null ? _a : 3;
    return unit * 1e3 + count;
  };
  var sortPlansByFrequency = (plans) => [...plans].sort((a, b) => planSortKey(a.label) - planSortKey(b.label));
  var isPrepaidPlan = (plan) => /prepaid/i.test((plan == null ? void 0 : plan.label) || "");
  var preferredPlan = (sortedPlans) => sortedPlans.find((p) => !isPrepaidPlan(p)) || sortedPlans[0];
  var findPlanById = (plans, id) => id == null ? void 0 : plans.find((p) => String(p.id) === String(id));

  // src/lib/compare-at-price.ts
  var computeCompareAtDiscount = (compareAtCents, priceCents) => {
    if (compareAtCents <= 0 || compareAtCents <= priceCents) return 0;
    return Math.round((compareAtCents - priceCents) / compareAtCents * 100);
  };
  var syncCompareAtInputs = (container, compareAtCents, priceCents) => {
    const capInput = container.querySelector(
      "[data-compare-at-price-input]"
    );
    const discInput = container.querySelector(
      "[data-compare-at-price-discount-input]"
    );
    if (capInput) capInput.value = String(compareAtCents || 0);
    if (discInput)
      discInput.value = String(computeCompareAtDiscount(compareAtCents, priceCents));
  };

  // src/buy-bar.ts
  (() => {
    let dppSubscriptionAvailable = true;
    let lastScrollY = window.scrollY;
    let scrollingDown = false;

    const formatMoney = (cents) => "$" + (cents / 100).toFixed(2);

    const parseSellingPlanDiscountMap = (raw) => {
      const map = /* @__PURE__ */ new Map();
      const text = String(raw || "").trim();
      if (!text) return map;
      text.split(",").forEach((segment) => {
        const parts = segment.trim().split("/").map((part) => part.trim());
        if (parts.length < 3) return;
        const [planId, initialKey, recurringKey] = parts;
        if (!planId) return;
        map.set(String(planId), { initialKey, recurringKey });
      });
      return map;
    };

    const renderTemplate = (template, productCents, totalCents, discountPercent) => {
      let text = template || "";
      if (!text) return "";
      const discount = Math.max(0, Math.round(Number(discountPercent) || 0));
      if (discount <= 0) {
        text = text.replace(/\s*\([^)]*\)/g, "").trimEnd();
      }
      return text
        .replace(/\{product_price\}/g, formatMoney(productCents))
        .replace(/\{total_price\}/g, formatMoney(totalCents != null ? totalCents : productCents))
        .replace(/\{discount\}/g, String(discount));
    };

    const getPdpPanel = () => document.querySelector(".product-purchase-panel");

    const syncTemplatesFromPdp = (bar) => {
      const panel = getPdpPanel();
      if (!panel) return;
      if (panel.dataset.atcTextTemplate) {
        bar.dataset.atcTextTemplate = panel.dataset.atcTextTemplate;
      }
      if (panel.dataset.subscribeTextTemplate) {
        bar.dataset.subscribeTextTemplate = panel.dataset.subscribeTextTemplate;
      }
      if (panel.dataset.sellingPlanDiscountMap) {
        bar.dataset.sellingPlanDiscountMap = panel.dataset.sellingPlanDiscountMap;
      }
    };

    const pdpAllowsSubscribe = () => {
      const panel = getPdpPanel();
      if (!panel) return true;
      return panel.dataset.showSubscribe !== "false";
    };

    const updateScrollVisibility = () => {
      const scrollY = window.scrollY;
      document.querySelectorAll("[data-buy-bar]").forEach((bar) => {
        if (!dppSubscriptionAvailable) {
          bar.classList.remove("buy-bar--visible");
          bar.setAttribute("aria-hidden", "true");
          return;
        }
        const threshold = parseInt(bar.dataset.threshold || "400", 10);
        const visible = scrollY > threshold && scrollingDown;
        bar.classList.toggle("buy-bar--visible", visible);
        bar.setAttribute("aria-hidden", visible ? "false" : "true");
      });
    };

    const onScroll = () => {
      const scrollY = window.scrollY;
      scrollingDown = scrollY > lastScrollY;
      lastScrollY = scrollY;
      updateScrollVisibility();
    };

    const resolveMappedPlanPricing = (plan, variantPlans, planDiscountMap) => {
      const mapping = planDiscountMap.get(String(plan.id));
      const discounts = variantPlans?.discounts;
      if (!mapping?.initialKey || !discounts || typeof discounts !== "object") {
        return { price: plan.price, discount: plan.discount };
      }
      const pct = Number(discounts[mapping.initialKey]) || 0;
      if (pct <= 0) {
        return { price: plan.price, discount: plan.discount };
      }
      const originalPrice = Number(variantPlans.price) || 0;
      const compareAt = Number(variantPlans.compareAt) || 0;
      const base = compareAt > 0 ? compareAt : originalPrice;
      const calculated = Math.round(base * (1 - pct / 100));
      const price = compareAt > originalPrice ? Math.min(calculated, originalPrice) : calculated;
      return { price, discount: pct };
    };

    const setSubscribeVisibility = (show) => {
      document.querySelectorAll("[data-buy-bar-subscribe]").forEach((el) => {
        el.hidden = !show;
      });
    };

    const updateSubscribeLabel = (bar, subscribeCents, discount, totalCents) => {
      const template = bar?.dataset.subscribeTextTemplate || "Subscribe - {product_price}";
      const label = renderTemplate(
        template,
        subscribeCents,
        totalCents != null ? totalCents : subscribeCents,
        discount
      );
      bar.querySelectorAll("[data-buy-bar-subscribe-label]").forEach((el) => {
        el.textContent = label;
      });
      bar.querySelectorAll("[data-buy-bar-discount-badge]").forEach((badge) => {
        badge.hidden = true;
      });
    };

    const updateAtcLabel = (bar, productCents, totalCents, discountPercent) => {
      const template = bar?.dataset.atcTextTemplate || "Add To Cart - {product_price}";
      const label = renderTemplate(
        template,
        productCents,
        totalCents != null ? totalCents : productCents,
        discountPercent
      );
      bar.querySelectorAll("[data-buy-bar-atc-label]").forEach((el) => {
        el.textContent = label;
      });
    };

    const setVariantIds = (variantId) => {
      document.querySelectorAll("[data-buy-bar-variant-id]").forEach((input) => {
        input.value = variantId;
      });
    };

    const setBuyBarPlan = (planId) => {
      document.querySelectorAll("[data-buy-bar-selling-plan]").forEach((input) => {
        input.value = planId || "";
      });
    };

    const setQuantity = (qty) => {
      const value = String(Math.max(1, parseInt(qty, 10) || 1));
      document.querySelectorAll("[data-buy-bar-quantity]").forEach((input) => {
        input.value = value;
      });
    };

    const syncAvailability = (available) => {
      document.querySelectorAll("[data-buy-bar-subscribe-btn], [data-buy-bar-atc-btn]").forEach((btn) => {
        btn.disabled = !available;
        btn.setAttribute("aria-disabled", String(!available));
      });
    };

    const initDppSubscribeSync = () => {
      const oilPlanInput = document.querySelector("[data-oil-selling-plan-id]");
      if (!oilPlanInput) return;
      const applyDppState = (hasSub, subscribePrice, discount) => {
        dppSubscriptionAvailable = hasSub;
        updateScrollVisibility();
        const allow = pdpAllowsSubscribe();
        setSubscribeVisibility(hasSub && allow);
        document.querySelectorAll("[data-buy-bar]").forEach((bar) => {
          syncTemplatesFromPdp(bar);
          if (hasSub && allow) {
            updateSubscribeLabel(bar, subscribePrice, discount);
            setBuyBarPlan(
              oilPlanInput.value || ""
            );
          }
        });
      };
      const selectedOption = document.querySelector(
        ".card-dropdown__option--selected"
      );
      const initialDiscount = parseInt(
        (selectedOption == null ? void 0 : selectedOption.dataset.sellingPlanDiscount) || "0",
        10
      );
      const initialPrice = parseInt(
        (selectedOption == null ? void 0 : selectedOption.dataset.subscribePrice) || "0",
        10
      );
      applyDppState(
        oilPlanInput.value !== "",
        initialPrice,
        initialDiscount
      );
      document.addEventListener("dropdown:change", (e) => {
        const { sellingPlanId, sellingPlanDiscount, subscribePrice } = e.detail || {};
        applyDppState(sellingPlanId !== "", subscribePrice, sellingPlanDiscount);
        if (sellingPlanId) setBuyBarPlan(String(sellingPlanId));
      });
    };

    const initPdpVariantSync = () => {
      if (document.querySelector("[data-oil-selling-plan-id]")) return;
      const plansEl = document.querySelector("[data-subscription-plans]");
      const panelPlanInput = document.querySelector("[data-selling-plan-input]");
      const variantsEl = document.querySelector("[data-variants-json]");
      const variants = variantsEl ? JSON.parse(variantsEl.textContent || "[]") : [];
      const planMap = plansEl ? parsePlanMap(plansEl.textContent) : {};

      const currentVariantId = () => {
        var _a;
        return ((_a = document.querySelector("[data-variant-id-input]")) == null ? void 0 : _a.value) || "";
      };

      const syncBuyBarCompareAt = (variantId) => {
        const v = variants.find((x) => String(x.id) === String(variantId));
        document.querySelectorAll("[data-buy-bar] form").forEach((form) => {
          syncCompareAtInputs(form, (v == null ? void 0 : v.compare_at_price) || 0, (v == null ? void 0 : v.price) || 0);
        });
      };

      const syncForVariant = (variantId) => {
        document.querySelectorAll("[data-buy-bar]").forEach((bar) => {
          syncTemplatesFromPdp(bar);
          const planDiscountMap = parseSellingPlanDiscountMap(
            bar.dataset.sellingPlanDiscountMap || ""
          );
          const allowSubscribe = pdpAllowsSubscribe();
          bar.dataset.showSubscribe = String(allowSubscribe);

          syncBuyBarCompareAt(variantId);
          setVariantIds(variantId);

          const v = variants.find((x) => String(x.id) === String(variantId));
          const available = !!(v && v.available);
          syncAvailability(available);

          const oncePrice = Number(v?.price) || 0;
          updateAtcLabel(bar, oncePrice, oncePrice);

          if (!plansEl || !panelPlanInput) {
            setSubscribeVisibility(false);
            return;
          }

          const vp = getVariantPlans(planMap, variantId);
          if (!hasPlans(vp) || !allowSubscribe) {
            setBuyBarPlan("");
            setSubscribeVisibility(false);
            return;
          }

          const plans = sortPlansByFrequency(vp.plans);
          const selectedId = panelPlanInput.dataset.sellingPlanId || panelPlanInput.value || "";
          const plan = findPlanById(plans, selectedId) || preferredPlan(plans);
          if (!plan) {
            setSubscribeVisibility(false);
            return;
          }

          const resolved = resolveMappedPlanPricing(plan, vp, planDiscountMap);
          setBuyBarPlan(String(plan.id));
          setSubscribeVisibility(true);
          updateSubscribeLabel(bar, resolved.price, resolved.discount, resolved.price);
        });
      };

      document.addEventListener("variant:change", (e) => {
        var _a;
        syncForVariant(((_a = e.detail) == null ? void 0 : _a.variantId) || currentVariantId());
      });

      document.addEventListener("dropdown:change", (e) => {
        const target = e.target;
        if (target.closest(".cart-item-upsell")) return;
        if (!target.closest("[data-subscription-frequency]")) return;
        syncForVariant(currentVariantId());
      });

      document.addEventListener("purchase-panel:pricing-change", (e) => {
        const pricing = e.detail?.pricing;
        if (!pricing) return;
        document.querySelectorAll("[data-buy-bar]").forEach((bar) => {
          syncTemplatesFromPdp(bar);
          const variantId = currentVariantId();
          const v = variants.find((x) => String(x.id) === String(variantId));
          const atcProduct = Number(v?.price) || 0;
          // {total_price} on the one-time bar button = main once price + add-ons (if any).
          const addonTotal = Number(pricing.addonDiscountedPrice) || 0;
          const atcTotal = atcProduct + addonTotal;
          const submitBtn = getPdpPanel()?.querySelector("[data-submit-btn]");
          const isSubscribe = !!submitBtn?.classList.contains("product-purchase-panel__btn--subscribe");
          const mappedOnce = Number(pricing.mainProductDiscount?.oneTimePercent) || 0;
          const mappedSubscribe = Number(pricing.mainProductDiscount?.subscribePercent) || 0;
          const activeDiscount = Math.max(0, Math.round(Number(pricing.discount) || 0));
          const onceDiscount = Math.max(
            0,
            Math.round(mappedOnce || (!isSubscribe ? activeDiscount : 0))
          );
          updateAtcLabel(bar, atcProduct, atcTotal, onceDiscount);

          const subscribeCents = parseInt(submitBtn?.getAttribute("data-subscribe-price") || "0", 10) || 0;
          const subscribeDiscount = Math.max(
            0,
            Math.round(
              mappedSubscribe ||
                (isSubscribe ? activeDiscount : 0) ||
                parseInt(submitBtn?.getAttribute("data-subscribe-discount") || "0", 10) ||
                0
            )
          );
          if (subscribeCents > 0 && pdpAllowsSubscribe()) {
            updateSubscribeLabel(bar, subscribeCents, subscribeDiscount, subscribeCents + addonTotal);
          }
        });
        const qtyInput = document.querySelector('.product-purchase-panel input[name="quantity"]');
        if (qtyInput) setQuantity(qtyInput.value);
      });

      document.addEventListener("quantity:change", (e) => {
        var _a;
        setQuantity(((_a = e.detail) == null ? void 0 : _a.quantity) || 1);
      });

      syncForVariant(currentVariantId());
    };

    const initCartAjax = () => {
      document.querySelectorAll("[data-buy-bar] form").forEach((form) => {
        form.addEventListener("submit", async (event) => {
          event.preventDefault();
          const submitBtn = form.querySelector('button[type="submit"]');
          if (submitBtn) submitBtn.disabled = true;
          try {
            const formData = new FormData(form);
            const id = formData.get("id");
            if (!id) throw new Error("Missing variant");
            const quantity = Math.max(1, parseInt(formData.get("quantity") || "1", 10));
            const sellingPlan = formData.get("selling_plan");
            // Quarterly (90-day) oils: the 40% discount rides on the `_quaterly_subs_key`
            // property and a forced quantity of 3 — same contract the PDP purchase panel
            // and the cart drawer use. The selling plan itself carries no price adjustment.
            const barEl = form.closest("[data-buy-bar]");
            const quarterlyPlanId = String(
              (barEl && barEl.dataset.quarterlySellingPlanId) || ""
            ).trim();
            const isQuarterlyPlan = !!sellingPlan && !!quarterlyPlanId && String(sellingPlan) === quarterlyPlanId;
            const item = {
              id: Number(id),
              quantity: isQuarterlyPlan ? 3 : quantity
            };
            // Only the subscribe form includes a selling_plan input.
            if (sellingPlan) {
              item.selling_plan = Number(sellingPlan);
            }
            const properties = {};
            if (isQuarterlyPlan) properties._quaterly_subs_key = "true";
            const compareAt = formData.get("properties[_compare_at_price]");
            const compareDisc = formData.get("properties[_compare_at_price_discount]");
            if (compareAt != null) properties._compare_at_price = String(compareAt);
            if (compareDisc != null) properties._compare_at_price_discount = String(compareDisc);
            if (Object.keys(properties).length) item.properties = properties;

            const response = await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json"
              },
              body: JSON.stringify({ items: [item] })
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
              throw new Error(data.description || data.message || "Could not add to cart");
            }
            await window.HotelCollection?.refreshCart?.();
            window.HotelCollection?.openCart?.();
            document.dispatchEvent(
              new CustomEvent("cart:item-added", {
                detail: { variantId: String(id), isUpsell: false }
              })
            );
          } catch (error) {
            console.error("[buy-bar]", error);
          } finally {
            if (submitBtn) submitBtn.disabled = false;
          }
        });
      });
    };

    const init = () => {
      document.querySelectorAll("[data-buy-bar]").forEach((bar) => {
        syncTemplatesFromPdp(bar);
        if (!pdpAllowsSubscribe()) {
          setSubscribeVisibility(false);
          bar.dataset.showSubscribe = "false";
        }
      });
      updateScrollVisibility();
      initDppSubscribeSync();
      initPdpVariantSync();
      initCartAjax();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("shopify:section:load", init);
    init();
  })();
})();
